<template>
  <Modal
    v-model:visible="isVisible"
    class="parcel-scheduling-success-modal"
    @keypress.esc="isVisible = false"
  >
    <template #header>
      <FontAwesomeIcon aria-hidden="true" :icon="['fal', 'check-circle']" />
      <h1>{{ t('common.success') }}</h1>
    </template>

    <p>
      {{ t('parcelScheduling.successfullyScheduled') }}
    </p>

    <h2 class="text-gray-darker uppercase">
      {{ t('referenceData.referenceNumbers', result.referenceNumbers.length) }}
    </h2>

    <ol class="m-0 p-0">
      <li
        v-for="referenceNumber in result.referenceNumbers"
        :key="referenceNumber.typeNaturalId"
        class="text-gray-darkest"
      >
        <KeyValuePair
          :label="referenceNumber.typeName"
          :value="referenceNumber.value"
          inline
        />
      </li>
    </ol>

    <h2 class="text-gray-darker uppercase">
      {{
        t('parcelScheduling.scheduledParam', {
          param: t('shipments.shipments', result.shipmentIds.length),
        })
      }}
    </h2>

    <ol class="m-0 p-0">
      <li v-for="id in result.shipmentIds" :key="id" class="text-gray-darkest">
        {{ t('shipments.shipmentNumParam', { param: id }) }}
      </li>
    </ol>

    <template #footer>
      <router-link v-if="result.shipmentIds.length" :to="searchRoute">
        <RygenButton
          mode="secondary"
          :label="
            t('common.viewParam', {
              param: t('shipments.shipments', result.shipmentIds.length),
            })
          "
          @click="isVisible = false"
        />
      </router-link>

      <RygenButton
        mode="secondary"
        icon="pi pi-print"
        :loading="isPrintingShippingLabels"
        :label="t('shipments.printShippingLabel')"
        @click="onBatchPrintShippingLabels"
      />

      <RygenButton
        mode="primary"
        icon="pi pi-check"
        :label="t('common.continue')"
        @click="isVisible = false"
      />
    </template>
    <PrintShippingLabelTemplateModal
      v-model:visible="isShowingPrintShippingLabelTemplateModal"
      @confirm="onBatchPrintShippingLabelsConfirm"
    />
  </Modal>
</template>

<script lang="ts" setup>
import axios from 'axios';
import { computed, PropType, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';

import api from '@/api';
import { ParcelSchedulingResponse, ShippingLabelFormat } from '@/model';
import { buildSearchRoute } from '@/util';
import {
  deliverShippingLabel,
  shippingLabelErrorDetail,
} from '@/util/ShippingLabelUtil';

import PrintShippingLabelTemplateModal from '@/components/shipment/PrintShippingLabelTemplateModal.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import KeyValuePair from '@rygen/rygen-ui/components/ui/KeyValuePair.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const props = defineProps({
  result: {
    type: Object as PropType<ParcelSchedulingResponse>,
    required: true,
  },
});
const { result } = toRefs(props);

const { t } = useI18n();
const toast = useToast();

const isShowingPrintShippingLabelTemplateModal = ref(false);
const isPrintingShippingLabels = ref(false);

const searchRoute = computed(() =>
  buildSearchRoute('shipmentList', { shipmentId: result.value.shipmentIds }),
);

const onBatchPrintShippingLabels = () => {
  isShowingPrintShippingLabelTemplateModal.value = true;
};

const onBatchPrintShippingLabelsConfirm = async (
  type: string,
  _templateId: number | undefined,
  format: ShippingLabelFormat,
) => {
  const numShipments = result.value.shipmentIds.length;

  try {
    isPrintingShippingLabels.value = true;

    const shipmentIds = result.value.shipmentIds;
    const blob = await api.shipmentDocumentBatch.generateBatchShippingLabels(
      shipmentIds,
      type,
      format,
    );
    deliverShippingLabel(blob, 'shippingLabels', format, {
      openInNewTab: false,
    });
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error('Error downloading shipping labels', e);
    toast.addError({
      detail:
        shippingLabelErrorDetail(e) ??
        t('shipments.errors.shippingLabelDownload', numShipments),
      error: e,
    });
  } finally {
    isPrintingShippingLabels.value = false;
  }
};
</script>

<style>
.parcel-scheduling-success-modal.p-dialog {
  .p-dialog-header {
    .modal-header {
      gap: var(--sp-2);
      align-items: center;
      font-size: 18px;

      svg {
        color: var(--success-color);
      }

      h1 {
        color: var(--brand-dark-color);
      }
    }
  }

  .p-dialog-content {
    padding-block: 0;

    p {
      color: var(--brand-dark-color);
    }

    h2 {
      margin: var(--sp-6) 0 var(--sp-3) 0;
      font-size: 12px;
      font-weight: 500;
    }

    ol {
      counter-reset: itemNum;

      li {
        &::before {
          content: counter(itemNum) '. ';
        }

        counter-increment: itemNum;
        padding: var(--sp-3) 0;
        border-top: var(--divider);
      }
    }
  }
}
</style>
